import { useMemo } from 'react';
import { useCommunications } from '../../../../hooks/useCommunications';

const RECENT_FETCH_LIMIT = 6;
const LAST_SHOWN = 3;
const RECENT_CONTACT_NAMES_SHOWN = 5;
const OPEN_PROBE_LIMIT = 1;

/** One fetch backs three surfaces (recent list, contact chips, count
 *  insight). `excludeId` drops the thread being edited from its own list. */
export function useRecentCommunications(practiceId: string | null, excludeId: string | undefined) {
    const { communications, meta, isLoading } = useCommunications({
        page: 1,
        limit: RECENT_FETCH_LIMIT,
        practice: practiceId ?? undefined,
        enabled: practiceId !== null
    });

    const others = useMemo(
        () => communications.filter((c) => c.id !== excludeId),
        [communications, excludeId]
    );

    const recentContactNames = useMemo(() => {
        const seen = new Set<string>();
        const names: string[] = [];
        for (const c of others) {
            const name = c.lastEntry?.authorName;
            if (name && !seen.has(name)) {
                seen.add(name);
                names.push(name);
                if (names.length >= RECENT_CONTACT_NAMES_SHOWN) break;
            }
        }
        return names;
    }, [others]);

    return {
        lastCommunications: others.slice(0, LAST_SHOWN),
        recentContactNames,
        totalCommunications: meta.total,
        isLoading
    };
}

/** A second, separate query (`status=open&limit=1`) - just reads `meta.total`. */
export function useOpenCommunicationsCount(practiceId: string | null): number {
    const { meta } = useCommunications({
        page: 1,
        limit: OPEN_PROBE_LIMIT,
        practice: practiceId ?? undefined,
        status: 'open',
        enabled: practiceId !== null
    });
    return meta.total;
}
